import type { Sport } from "@/hooks/firestore/models/sports";
import type { Criteria } from "../types/bet-tracker";
import type { BetEntry } from "@/hooks/firestore/models/entry";
import { isSameEventMultiSelection } from "./type-guards";

export const doesValueSatisfyTarget = (
  value: number | string,
  criteria?: Criteria,
) => {
  if (!criteria) return false;

  if (typeof value === "string") {
    return value === criteria?.COMPETITOR_EQ;
  }

  if (criteria?.GT !== undefined && value <= criteria.GT) {
    return false;
  }

  if (criteria?.GTE !== undefined && value < criteria.GTE) {
    return false;
  }

  if (criteria?.LT !== undefined || criteria?.LTE !== undefined) {
    return false;
  }

  if (criteria?.EQ !== undefined && value !== criteria.EQ) {
    return false;
  }

  return true;
};

export const doesValueFailTarget = (
  value: number | string,
  criteria?: Criteria,
): boolean => {
  if (typeof value === "string")
    return (
      typeof criteria?.COMPETITOR_EQ === "string" &&
      value.length > 0 &&
      value !== criteria?.COMPETITOR_EQ
    );

  if (criteria?.LT !== undefined && value >= criteria.LT) return true;
  if (criteria?.LTE !== undefined && value > criteria.LTE) return true;
  if (criteria?.EQ !== undefined && value > criteria.EQ) return true;

  return false;
};

export const deriveInterimResult = (
  value?: string | number | null,
  criteria?: Criteria,
): "WIN" | "LOSE" | "UNDECIDED" => {
  if (value == null) return "UNDECIDED";

  if (doesValueSatisfyTarget(value, criteria)) return "WIN";
  if (doesValueFailTarget(value, criteria)) return "LOSE";

  return "UNDECIDED";
};

export const getValidSportPeriods = (sport?: Sport) => {
  if (!sport) return [];

  switch (sport) {
    case "RUGBY_LEAGUE":
    case "SOCCER":
      return ["H1", "H2"];
    case "AUSSIE_RULES":
    case "AMERICAN_FOOTBALL":
    case "BASKETBALL":
      return ["Q1", "Q2", "Q3", "Q4"];
    case "ICE_HOCKEY":
      return ["P1", "P2", "P3"];
    default:
      return [];
  }
};

export const SPORTS_FOR_HOME_AWAY_FALLBACKS: Sport[] = [
  "AUSSIE_RULES",
  "RUGBY_LEAGUE",
  "SOCCER",
  "CRICKET",
  "RUGBY",
];

export const hasTrackableBets = (entry: BetEntry) => {
  return entry.data.selections?.some((selection) => {
    if (selection.event.eventType === "RACE") return false;

    if (isSameEventMultiSelection(selection)) {
      return Object.values(selection.outcomes ?? {})?.some(
        (outcome) => !!outcome.winCriteria,
      );
    }

    // When the backend supports non SEMs, check for
    // winCriteria on single selections as well
    return false;
  });
};
